import { useCallback, useMemo, useState } from 'react'
import { EditOutlined } from '@ant-design/icons'
import { Button, Table } from 'antd'
import { Link } from 'react-router-dom'
import { ListError, PageHeader } from '../Common'
import { useAsync, useIsMobile } from '../../hooks'
import api from '../../api'
import Filters from '../Forms/Filters'
import { toPath } from '../../routes'
import codes from './codes'
import {
  DeviceTypeMobileCard,
  DeviceTypeMobileDetails,
  EXPAND_COLUMN_WIDTH,
} from './DeviceTypeMobileRow'

const COLUMNS = [
  {
    title: 'Device Type',
    dataIndex: ['name'],
    key: 'name',
  },
  ...codes.map(({ name, attribute }) => ({
    title: name,
    dataIndex: attribute,
    key: attribute,
  })),
  {
    title: 'Actions',
    key: 'actions',
    render: (text, record) => (
      <span>
        <Link
          to={toPath.deviceTypes.deviceType.edit({ id: record.id })}
          className="edit-button"
        >
          <Button type="link" icon={<EditOutlined />} />
        </Link>
      </span>
    ),
  },
]

// On mobile the seven code columns don't fit, so each row collapses into a
// card: the device type name plus its primary billing pair stay visible and the
// full set of codes moves behind a per-row expand toggle.
const MOBILE_COLUMNS = [
  {
    title: 'Device Type',
    key: 'deviceType',
    render(_, deviceType) {
      return <DeviceTypeMobileCard deviceType={deviceType} />
    },
  },
]

// Defined at module scope so the antd render prop isn't flagged as an
// inline-defined component (it just returns an existing component element).
const renderMobileDetails = deviceType => (
  <DeviceTypeMobileDetails deviceType={deviceType} />
)

const MOBILE_EXPANDABLE = {
  columnWidth: EXPAND_COLUMN_WIDTH,
  expandedRowRender: renderMobileDetails,
}

// All except the last column
const SEARCHABLE = COLUMNS.slice(0, COLUMNS.length - 1)

function filter(value, search) {
  return (value || '').toLowerCase().includes(search.toLowerCase())
}

function filterDeviceType(deviceType, search) {
  return !!SEARCHABLE.find(({ dataIndex }) =>
    filter(deviceType[dataIndex], search),
  )
}

export default function DeviceTypes() {
  const isMobile = useIsMobile()
  const fetchDeviceTypes = useCallback(() => api.deviceTypes.list(), [])
  const {
    pending,
    error,
    clearError,
    value: deviceTypes,
  } = useAsync(fetchDeviceTypes)
  const [queryFilter, setQueryFilter] = useState('')
  const onSubmit = useCallback(({ query }) => {
    setQueryFilter(query)
  }, [])

  const deviceTypesFiltered = useMemo(
    () =>
      (deviceTypes || []).filter(deviceType =>
        filterDeviceType(deviceType, queryFilter),
      ),
    [deviceTypes, queryFilter],
  )

  return (
    <div>
      <PageHeader title="CPT Codes" />
      {error && <ListError description={error.message} onClose={clearError} />}
      <Filters onSubmit={onSubmit} showSearch />
      {/* Desktop keeps antd's default density: this table never set `size`. */}
      <Table
        size={isMobile ? 'small' : undefined}
        columns={isMobile ? MOBILE_COLUMNS : COLUMNS}
        showHeader={!isMobile}
        expandable={isMobile ? MOBILE_EXPANDABLE : undefined}
        dataSource={deviceTypesFiltered}
        loading={pending}
        rowKey="id"
      />
    </div>
  )
}
